/**
 * Haus-Vorlagen für den Kampagnen-Editor („HTML-Vorlagen").
 *
 * Gebaut aus demselben Baukasten wie die Mails aus dem Assistenten
 * (render.ts, Brand-Design v2.1, Dark-Mode-Schutz), damit alles gleich
 * aussieht. Jede Vorlage gibt es in drei Emoji-Stufen — ohne, normal und
 * viele, passend zum Thema. Platzhalter stehen in eckigen Klammern und in
 * Großbuchstaben; der Versand-Check meldet, wenn einer übrig bleibt.
 *
 * In der Datenbank (Mail_templates) steht die Stufe „normal" in html_content,
 * die anderen beiden in varianten ({ haus, ohne, viele }). Admins erzeugen
 * die Zeilen auf der Seite „HTML-Vorlagen" neu, wenn sich hier etwas ändert.
 */
import { renderMail, type FactRow, type MailDoc } from './render';
import type { EmojiStufe } from './vorlagen';

/** Kategorien für alle HTML-Vorlagen (Haus- und eigene Vorlagen). */
export const VORLAGEN_KATEGORIEN: { wert: string; label: string }[] = [
  { wert: 'lehrgang', label: 'Lehrgang' },
  { wert: 'pruefung', label: 'Prüfung' },
  { wert: 'event', label: 'Event' },
  { wert: 'fight-night', label: 'Fight Night' },
  { wert: 'camp', label: 'Camp' },
  { wert: 'newsletter', label: 'Newsletter' },
  { wert: 'ankundigung', label: 'Ankündigung' },
  { wert: 'basic', label: 'Basic' },
];

export function kategorieText(wert: string | null | undefined): string {
  return VORLAGEN_KATEGORIEN.find((k) => k.wert === wert)?.label ?? wert ?? '';
}

export interface HausVorlage {
  key: string;
  name: string;
  beschreibung: string;
  /** Wert aus VORLAGEN_KATEGORIEN */
  kategorie: string;
  /** Symbol für die Galerie */
  emoji: string;
  bauen: (stufe: EmojiStufe) => MailDoc;
}

const e = (stufe: EmojiStufe, emoji: string) => (stufe === 'viele' ? `${emoji} ` : '');
const kopf = (stufe: EmojiStufe, einfach: string, viele: string) => (stufe === 'ohne' ? '' : stufe === 'viele' ? viele : einfach);
const gruss = (stufe: EmojiStufe, anrede: string) => (stufe === 'ohne' ? `${anrede},` : `${anrede} 👋,`);
const team = (stufe: EmojiStufe) => (stufe === 'ohne' ? 'Euer Randori-Pro Team [STANDORT]' : 'Euer Randori-Pro Team [STANDORT] 🥋');
const schluss = (stufe: EmojiStufe, text: string) => (stufe === 'viele' ? `${text} 🥋` : text);

const FAKT_SYMBOL: Record<string, string> = {
  Wann: '📅', Datum: '📅', Zeitraum: '📅', Wo: '📍', 'Für wen': '👥', Kosten: '💶', Gebühr: '💶',
  Ausrüstung: '🎒', Anmeldeschluss: '⏰', Einlass: '🚪', Anreise: '🚌', Trainer: '🥋',
};
function fakten(stufe: EmojiStufe, rows: FactRow[]): FactRow[] {
  return stufe === 'viele' ? rows.map((r) => (FAKT_SYMBOL[r.label] ? { ...r, label: `${FAKT_SYMBOL[r.label]} ${r.label}` } : r)) : rows;
}

const KONTAKT: MailDoc['contact'] = { email: '[STANDORT]@randori-pro.de', place: '[STANDORT]' };

const LEHRGANG: HausVorlage = {
  key: 'lehrgang',
  name: 'Lehrgang oder Seminar',
  beschreibung: 'Einladung zu einem Lehrgang, Seminar oder Spezialtraining mit Gasttrainer: Programm, Eckdaten, Anmeldung.',
  kategorie: 'lehrgang',
  emoji: '🥋',
  bauen: (s) => ({
    title: '[TITEL DES LEHRGANGS]',
    preheader: '[DATUM] · [UHRZEIT] · mit [NAME DES TRAINERS]',
    hero: {
      emoji: kopf(s, '🥋', '🥋💪'),
      badge: 'Lehrgang · [STANDORT]',
      title: '[TITEL DES LEHRGANGS]',
      subtitle: 'mit [NAME DES TRAINERS] · [DAN-GRAD]',
      highlight: `${e(s, '⚡')}[DATUM] · [UHRZEIT]`,
    },
    greeting: gruss(s, 'Hey {{ contact.VORNAME }}'),
    intro: '[ZWEI, DREI SÄTZE: WORUM GEHT ES, WAS NEHMEN DIE TEILNEHMER MIT?]\n\n[WER IST DER TRAINER, WARUM LOHNT SICH DER LEHRGANG?]',
    sections: [
      { type: 'cards', heading: `${e(s, '✨')}Das erwartet Euch`, items: [
        { title: '[SCHWERPUNKT 1]', text: '[EIN SATZ DAZU]' },
        { title: '[SCHWERPUNKT 2]', text: '[EIN SATZ DAZU]' },
        { title: '[SCHWERPUNKT 3]', text: '[EIN SATZ DAZU]' },
      ] },
      { type: 'facts', heading: `${e(s, '📋')}Alle Infos auf einen Blick`, rows: fakten(s, [
        { label: 'Wann', value: '[DATUM] · [UHRZEIT]' },
        { label: 'Wo', value: 'Randori-Pro [STANDORT]', sub: '[ADRESSE]' },
        { label: 'Trainer', value: '[NAME] · [DAN-GRAD]' },
        { label: 'Für wen', value: '[ZIELGRUPPE, Z. B. AB 14 JAHREN]' },
        { label: 'Kosten', value: '[BETRAG ODER KOSTENLOS]' },
        { label: 'Ausrüstung', value: '[WAS MITBRINGEN]' },
      ]) },
      { type: 'cta', title: 'Platz sichern', text: 'Anmeldung bis [ANMELDESCHLUSS].', buttonText: 'Jetzt anmelden', url: 'https://randori-pro.de', footnote: '[HINWEIS, Z. B. BEGRENZTE PLÄTZE]' },
      { type: 'box', tone: 'important', title: `${e(s, '⚠️')}Bitte beachten`, text: '[WICHTIGER HINWEIS, Z. B. VORAUSSETZUNG ODER AUSRÜSTUNG]' },
    ],
    closing: schluss(s, 'Wir freuen uns auf Euch!'),
    signature: { greeting: 'Sportliche Grüße', team: team(s) },
    contact: KONTAKT,
    emojis: s,
  }),
};

const PRUEFUNG: HausVorlage = {
  key: 'guertelpruefung',
  name: 'Gürtelprüfung',
  beschreibung: 'Prüfungstag mit Gruppen und Uhrzeiten, Voraussetzungen, Packliste und Hinweis zum Zuschauen.',
  kategorie: 'pruefung',
  emoji: '⭐',
  bauen: (s) => ({
    title: 'Gürtelprüfung [DATUM]',
    preheader: '[DATUM] — alle Gruppen und Uhrzeiten',
    hero: {
      emoji: kopf(s, '🥋', '🥋⭐'),
      badge: 'Gürtelprüfung · [STANDORT]',
      title: 'Eure Gürtelprüfung',
      highlight: `${e(s, '⚡')}[DATUM] · Einlass [UHRZEIT]`,
    },
    greeting: gruss(s, 'Liebe Eltern'),
    intro: 'bald ist es so weit: Am **[DATUM]** zeigen unsere Prüflinge, was sie gelernt haben. Hier findet Ihr alle Gruppen, Uhrzeiten und was Ihr mitbringen müsst.',
    sections: [
      { type: 'facts', heading: `${e(s, '📋')}Der Prüfungstag`, rows: fakten(s, [
        { label: 'Datum', value: '[DATUM]' },
        { label: 'Einlass', value: '[UHRZEIT]' },
        { label: 'Wo', value: 'Randori-Pro [STANDORT]', sub: '[ADRESSE]' },
        { label: 'Gebühr', value: '[BETRAG, Z. B. BAR AM PRÜFUNGSTAG]' },
      ]) },
      { type: 'cards', heading: `${e(s, '🕐')}Gruppen und Uhrzeiten`, items: [
        { title: '[GRUPPE 1, Z. B. WEISS-GELB KIDS 6–9]', text: '[UHRZEIT]' },
        { title: '[GRUPPE 2]', text: '[UHRZEIT]' },
        { title: '[GRUPPE 3]', text: '[UHRZEIT]' },
      ] },
      { type: 'list', heading: `${e(s, '📋')}Voraussetzung für die Prüfung`, items: ['[VORAUSSETZUNG 1, Z. B. VORPRÜFUNG BESTANDEN]', '[VORAUSSETZUNG 2]'] },
      { type: 'list', heading: `${e(s, '🎒')}Bitte mitbringen`, items: ['Sauberer Gi mit aktuellem Gürtel', 'Trinkflasche', '[WEITERES]'] },
      { type: 'box', title: `${e(s, '👀')}Zuschauen`, text: '[DÜRFEN ELTERN ZUSCHAUEN? Z. B. NUR ZUR ÜBERGABE AM ENDE]' },
    ],
    closing: schluss(s, 'Wir drücken allen die Daumen — Ihr schafft das!'),
    signature: { greeting: 'Sportliche Grüße', team: team(s) },
    contact: KONTAKT,
    emojis: s,
  }),
};

const EVENT: HausVorlage = {
  key: 'event',
  name: 'Event oder Wettkampf',
  beschreibung: 'Fight Night, Turnier oder Aktionstag: drei Höhepunkte, Eckdaten und Knopf zu Tickets oder Anmeldung.',
  kategorie: 'event',
  emoji: '🥊',
  bauen: (s) => ({
    title: '[NAME DES EVENTS]',
    preheader: '[DATUM] · [ORT] — jetzt Tickets sichern',
    hero: {
      emoji: kopf(s, '🥊', '🥊🔥'),
      badge: 'Event · [DATUM]',
      title: '[NAME DES EVENTS]',
      subtitle: '[UNTERZEILE, Z. B. DIE FIGHT NIGHT IN BERLIN]',
      highlight: `${e(s, '⚡')}[DATUM] · [UHRZEIT] · [ORT]`,
    },
    greeting: gruss(s, 'Hey {{ contact.VORNAME }}'),
    intro: '[ZWEI, DREI SÄTZE, DIE LUST MACHEN: WAS PASSIERT, WARUM MAN DABEI SEIN SOLLTE?]',
    sections: [
      { type: 'cards', heading: `${e(s, '🔥')}Darauf könnt Ihr Euch freuen`, items: [
        { title: '[HÖHEPUNKT 1]', text: '[EIN SATZ DAZU]' },
        { title: '[HÖHEPUNKT 2]', text: '[EIN SATZ DAZU]' },
        { title: '[HÖHEPUNKT 3]', text: '[EIN SATZ DAZU]' },
      ] },
      { type: 'facts', heading: `${e(s, '📋')}Alle Infos auf einen Blick`, rows: fakten(s, [
        { label: 'Wann', value: '[DATUM] · [UHRZEIT]' },
        { label: 'Wo', value: '[ORT]', sub: '[ADRESSE]' },
        { label: 'Kosten', value: '[TICKETPREIS]' },
        { label: 'Anmeldeschluss', value: '[DATUM]' },
      ]) },
      { type: 'cta', title: 'Seid dabei', text: '[EIN SATZ ZU TICKETS ODER ANMELDUNG]', buttonText: 'Tickets & Infos', url: 'https://randori-pro.de' },
    ],
    closing: schluss(s, 'Wir sehen uns!'),
    signature: { greeting: 'Sportliche Grüße', team: team(s) },
    contact: KONTAKT,
    emojis: s,
  }),
};

const CAMP: HausVorlage = {
  key: 'trainingslager',
  name: 'Trainingslager oder Camp',
  beschreibung: 'Mehrtägiges Camp oder Trainingslager: Zeitraum, Anreise, Ablauf, Packliste und Anmeldung.',
  kategorie: 'camp',
  emoji: '🏕️',
  bauen: (s) => ({
    title: '[NAME DES CAMPS]',
    preheader: '[ZEITRAUM] · [ORT] — alle Infos und Anmeldung',
    hero: {
      emoji: kopf(s, '🏕️', '🥋🏕️'),
      badge: 'Camp · [STANDORT]',
      title: '[NAME DES CAMPS]',
      subtitle: '[UNTERZEILE, Z. B. FÜR KIDS VON 6 BIS 13 JAHREN]',
      highlight: `${e(s, '⚡')}[ZEITRAUM]`,
    },
    greeting: gruss(s, 'Liebe Eltern'),
    intro: '[ZWEI, DREI SÄTZE: WAS ERWARTET DIE KINDER, WAS MACHT DAS CAMP AUS?]',
    sections: [
      { type: 'facts', heading: `${e(s, '📋')}Alle Infos auf einen Blick`, rows: fakten(s, [
        { label: 'Zeitraum', value: '[VON] bis [BIS]' },
        { label: 'Wo', value: '[ORT]', sub: '[ADRESSE]' },
        { label: 'Anreise', value: '[TREFFPUNKT UND UHRZEIT]' },
        { label: 'Für wen', value: '[ALTER ODER GRUPPE]' },
        { label: 'Kosten', value: '[BETRAG UND BEZAHLWEG]' },
      ]) },
      { type: 'steps', heading: `${e(s, '🗓️')}So läuft es ab`, items: ['[TAG 1: ANKUNFT UND ERSTES TRAINING]', '[TAG 2: …]', '[LETZTER TAG: ABHOLUNG UM …]'] },
      { type: 'list', heading: `${e(s, '🎒')}Packliste`, items: ['Trainingskleidung und Gi', 'Hallenschuhe und Trinkflasche', '[WEITERES]'] },
      { type: 'cta', title: 'Platz sichern', text: 'Anmeldung bis [ANMELDESCHLUSS].', buttonText: 'Jetzt anmelden', url: 'https://randori-pro.de', footnote: '[HINWEIS, Z. B. BEGRENZTE PLÄTZE]' },
      { type: 'box', tone: 'important', title: `${e(s, '⚠️')}Wichtig`, text: '[Z. B. WAS PASSIERT BEI ZU WENIGEN ANMELDUNGEN]' },
    ],
    closing: schluss(s, 'Wir freuen uns auf ein tolles Camp mit Euch!'),
    signature: { greeting: 'Sportliche Grüße', team: team(s) },
    contact: KONTAKT,
    emojis: s,
  }),
};

const NEWSLETTER: HausVorlage = {
  key: 'newsletter',
  name: 'Newsletter',
  beschreibung: 'Mehrere Themen in einer Mail: Neuigkeiten des Monats in drei Kästen, dazu ein Knopf nach Wunsch.',
  kategorie: 'newsletter',
  emoji: '📰',
  bauen: (s) => ({
    title: 'Neues aus der Randori-Pro Familie',
    preheader: '[MONAT]: [THEMA 1], [THEMA 2] und mehr',
    hero: {
      emoji: kopf(s, '📰', '📰🥋'),
      badge: 'Newsletter · [MONAT]',
      title: 'Neues aus der Randori-Pro Familie',
      subtitle: '[UNTERZEILE, Z. B. DAS WAR LOS IM SEPTEMBER]',
    },
    greeting: gruss(s, 'Hey {{ contact.VORNAME }}'),
    intro: '[EIN, ZWEI SÄTZE ZUR BEGRÜSSUNG]',
    sections: [
      { type: 'cards', heading: `${e(s, '📌')}Die Themen`, items: [
        { title: '[THEMA 1]', text: '[ZWEI, DREI SÄTZE]' },
        { title: '[THEMA 2]', text: '[ZWEI, DREI SÄTZE]' },
        { title: '[THEMA 3]', text: '[ZWEI, DREI SÄTZE]' },
      ] },
      { type: 'cta', title: '[TITEL DES KNOPF-KASTENS]', text: '[EIN SATZ DAZU]', buttonText: 'Mehr erfahren', url: 'https://randori-pro.de' },
    ],
    closing: schluss(s, 'Bis bald auf der Matte!'),
    signature: { greeting: 'Sportliche Grüße', team: team(s) },
    contact: { text: 'Bei Fragen wendet Euch bitte an den Tresen Eurer Sportschule.' },
    emojis: s,
  }),
};

export const HAUS_VORLAGEN: HausVorlage[] = [LEHRGANG, PRUEFUNG, EVENT, CAMP, NEWSLETTER];

export function hausVorlage(key: string | null | undefined): HausVorlage | null {
  return HAUS_VORLAGEN.find((v) => v.key === key) ?? null;
}

export function hausHtml(v: HausVorlage, stufe: EmojiStufe): string {
  return renderMail(v.bauen(stufe));
}
